import type { ScholarshipPlayer } from '../../../../types/scholarships';

export interface RoleColumn {
  role: string;
  pitchers: ScholarshipPlayer[];
}

/**
 * The read-only role summary: one column per role, pitchers listed under their role in staff
 * order (the order passed in). The board's roles lead, in the board's order, so the columns read
 * the same on every visit; a role a pitcher still carries after it was removed from the board's
 * list gets its own column after them rather than vanishing, since the row still says it.
 */
export const groupPitchersByRole = (
  staff: readonly ScholarshipPlayer[],
  roles: readonly string[],
): RoleColumn[] => {
  const columns = new Map<string, ScholarshipPlayer[]>(roles.map((r) => [r, []]));
  for (const pitcher of staff) {
    const role = pitcher.pitchingRole?.trim();
    if (!role) continue;
    const bucket = columns.get(role);
    if (bucket) bucket.push(pitcher);
    else columns.set(role, [pitcher]);
  }
  return Array.from(columns, ([role, pitchers]) => ({ role, pitchers }));
};

export interface PitchShapeTotal {
  shape: string;
  count: number;
}

/**
 * How many pitchers on the staff throw each shape. Every shape on the board is listed, zeros
 * included, because a zero is the point: "no sinker on the staff" should show up at a glance.
 * A shape a pitcher carries that the board no longer lists is counted after them. A pitcher who
 * has the same shape twice is counted once.
 */
export const countPitchShapes = (
  staff: readonly ScholarshipPlayer[],
  shapes: readonly string[],
): PitchShapeTotal[] => {
  const counts = new Map<string, number>(shapes.map((s) => [s, 0]));
  for (const pitcher of staff) {
    for (const shape of new Set(pitcher.pitchShapes ?? [])) {
      counts.set(shape, (counts.get(shape) ?? 0) + 1);
    }
  }
  return Array.from(counts, ([shape, count]) => ({ shape, count }));
};

/** Last name only, for the compact role summary. Falls back to the full name when there is none. */
export const summaryName = (player: Pick<ScholarshipPlayer, 'firstName' | 'lastName'>): string =>
  player.lastName.trim() || player.firstName;
